import React, { useState } from 'react';
import BiometricScanner from './BiometricScanner.jsx';
import { api } from '../api/client.js';
import { captureVoiceIndices, EF_VOICE_SCRIPT_ES } from '../utils/voiceFeatures.js';
import { cancelSpeech, speakEs } from '../utils/voiceUx.js';
import { GlossaryText } from './GlossaryTerm.jsx';

/**
 * Paso opt-in: primera fotografía del Estado Funcional (t0).
 * Mismo lenguaje visual que encuesta de manada. Skip total.
 */
export default function FunctionalBaselineStep({ onDone, onSkip }) {
    const [subStep, setSubStep] = useState('intro'); // intro | face | voice | saving | error
    const [faceData, setFaceData] = useState(null);
    const [voiceData, setVoiceData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [voiceProgress, setVoiceProgress] = useState(false);
    const [ttsOn, setTtsOn] = useState(true);

    function handleFace(data) {
        setFaceData(data || null);
        setSubStep('voice');
    }

    function skipFace() {
        setFaceData(null);
        setSubStep('voice');
    }

    async function runVoice() {
        setError('');
        setBusy(true);
        setVoiceProgress(true);
        cancelSpeech();
        try {
            const result = await captureVoiceIndices({
                durationMs: 8000,
                scriptId: EF_VOICE_SCRIPT_ES.id,
            });
            if (!result.quality.ok) {
                setError('No se oyó con claridad. Puedes reintentar u omitir.');
                setVoiceData(null);
            } else {
                setVoiceData(result);
                try {
                    await api.postLabsVoiceIndices({
                        source: 'profile_quiz_t0',
                        scriptId: EF_VOICE_SCRIPT_ES.id,
                        indices: result.indices,
                        quality: result.quality,
                        vector: result.vector,
                    });
                } catch {
                    /* labs opcional */
                }
            }
        } catch (err) {
            setError(err.message || 'No se pudo usar el micrófono');
            setVoiceData(null);
        } finally {
            setBusy(false);
            setVoiceProgress(false);
        }
    }

    function readScriptAloud() {
        speakEs(EF_VOICE_SCRIPT_ES.text, ttsOn);
    }

    async function saveBaseline() {
        setBusy(true);
        setError('');
        setSubStep('saving');
        try {
            const cameraGranted = !!faceData;
            const microphoneGranted = !!voiceData;
            const consent = await api.postFunctionalConsent({
                cameraGranted,
                microphoneGranted,
                purpose: 'estado_funcional_cambio',
            });
            const consentId = consent?.data?.id ?? consent?.id;

            const dims = {
                somatic: {},
                biometric: {},
                contextual: { source: 'profile_quiz_t0' },
            };
            const coverage = {
                dimensions: {
                    somatic: false,
                    linguistic: false,
                    biometric_face: !!faceData,
                    biometric_voice: !!voiceData,
                    organizational: false,
                    operational: false,
                    contextual: true,
                    historical: false,
                },
                channels: [],
            };
            if (faceData) {
                dims.biometric.face = {
                    indices: {
                        tension: faceData.tension ?? faceData.stressScore ?? null,
                        feeling: faceData.feeling ?? null,
                        dominantExpression: faceData.dominantExpression ?? null,
                        blinkRate: faceData.bpm ?? null,
                        eyelidOpening: faceData.ear ?? null,
                    },
                };
                coverage.channels.push('face');
            }
            if (voiceData) {
                dims.biometric.voice = {
                    indices: voiceData.indices,
                    vector: voiceData.vector,
                };
                coverage.channels.push('voice');
            }
            coverage.coverage_ratio =
                (Number(!!faceData) + Number(!!voiceData) + 1) / 7;

            if (!faceData && !voiceData) {
                onSkip?.();
                return;
            }

            const idempotencyKey = `t0-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
            await api.postFunctionalSnapshot({
                kind: 'initial',
                idempotencyKey,
                consentId,
                dims,
                coverage,
                quality: {
                    face: faceData ? { ok: true } : { skipped: true },
                    voice: voiceData?.quality || { skipped: true },
                },
                engineVersion: '1.0.0+ef-core-s2',
            });
            onDone?.();
        } catch (err) {
            setError(err.message || 'No se pudo guardar la referencia');
            setSubStep('voice');
        } finally {
            setBusy(false);
        }
    }

    if (subStep === 'intro') {
        return (
            <div className="container animate-in profile-quiz-page" style={{ maxWidth: '560px', padding: '3rem 1.5rem', textAlign: 'center' }}>
                <h2 className="mb-md" style={{ fontWeight: 600 }}>
                    Referencia de acompañamiento
                </h2>
                <div className="glass-card mb-lg" style={{ padding: '1.5rem', background: 'rgba(255,255,255,0.9)', textAlign: 'left' }}>
                    <p style={{ fontSize: '1.05rem', lineHeight: 1.65, color: '#334155', margin: 0 }}>
                        <GlossaryText>
                            Si quieres, podemos guardar una referencia opcional de rostro y voz solo como números
                            (no guardamos video ni audio). Sirve para notar cambios en el tiempo, no para juzgarte.
                            Puedes omitirlo y seguir igual.
                        </GlossaryText>
                    </p>
                </div>
                <div className="profile-quiz-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    <button type="button" className="btn btn-primary btn-lg" onClick={() => setSubStep('face')}>
                        Continuar (opcional)
                    </button>
                    <button type="button" className="btn btn-secondary btn-lg" onClick={() => onSkip?.()} disabled={busy}>
                        Omitir y seguir
                    </button>
                </div>
            </div>
        );
    }

    if (subStep === 'face') {
        return (
            <div className="container animate-in profile-quiz-page" style={{ maxWidth: '560px', padding: '2rem 1.5rem', textAlign: 'center' }}>
                <h2 className="mb-sm" style={{ fontWeight: 600 }}>
                    Rostro (opcional)
                </h2>
                <p className="text-muted mb-md" style={{ lineHeight: 1.5 }}>
                    Solo índices en tu dispositivo. Sin video en el servidor.
                </p>
                <BiometricScanner metricsOnly onDataCaptured={handleFace} />
                <button type="button" className="btn btn-secondary mt-md" onClick={skipFace}>
                    Continuar sin cámara
                </button>
            </div>
        );
    }

    return (
        <div className="container animate-in profile-quiz-page" style={{ maxWidth: '560px', padding: '2rem 1.5rem', textAlign: 'center' }}>
            <h2 className="mb-sm" style={{ fontWeight: 600 }}>
                {EF_VOICE_SCRIPT_ES.title} (opcional)
            </h2>
            <p className="text-muted mb-md" style={{ lineHeight: 1.5 }}>
                Lee en voz alta el texto (~{EF_VOICE_SCRIPT_ES.secondsHint}s). No se guarda el audio.
            </p>
            <div className="glass-card mb-lg" style={{ padding: '1.25rem', textAlign: 'left', background: 'rgba(255,255,255,0.92)' }}>
                <p style={{ margin: 0, lineHeight: 1.65, color: '#334155', fontSize: '1.05rem' }}>{EF_VOICE_SCRIPT_ES.text}</p>
            </div>
            <div className="profile-quiz-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                        setTtsOn((v) => {
                            const next = !v;
                            if (!next) cancelSpeech();
                            return next;
                        });
                    }}
                >
                    {ttsOn ? 'Leyendo en voz alta: on' : 'Leer en voz alta: off'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={readScriptAloud} disabled={!ttsOn || busy}>
                    Escuchar el texto
                </button>
            </div>
            {voiceProgress && <p className="text-muted mb-md">Escuchando…</p>}
            {voiceData?.quality?.ok && (
                <p className="text-sm mb-md" style={{ color: 'var(--color-primary-dark)' }}>
                    Lectura registrada (solo números).
                </p>
            )}
            {error && (
                <p className="text-sm mb-md" style={{ color: '#b45309' }}>
                    {error}
                </p>
            )}
            <div className="profile-quiz-actions" style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button type="button" className="btn btn-primary btn-lg" onClick={runVoice} disabled={busy}>
                    {voiceData ? 'Repetir lectura' : 'Grabar lectura'}
                </button>
                <button
                    type="button"
                    className="btn btn-primary btn-lg"
                    onClick={saveBaseline}
                    disabled={busy || (!faceData && !voiceData)}
                >
                    {busy && subStep === 'saving' ? 'Guardando…' : 'Guardar referencia'}
                </button>
                <button type="button" className="btn btn-secondary btn-lg" onClick={() => onSkip?.()} disabled={busy}>
                    Omitir y seguir
                </button>
            </div>
        </div>
    );
}
